import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { TopBar } from "@/components/chrome/TopBar";
import { probeMe } from "@/lib/shell";
import { legacyDestination, parseLegacyLanding, resolveLegacyTarget } from "@/lib/legacyRedirects";

/**
 * Where an old subdomain's URL lands after the cut-off (KET-3492).
 *
 * The Cloudflare rules know one thing — which host the person came from — and
 * hand the original path through untouched. The mapping from that path to a
 * portal route is derived HERE, from the same `/v1/me` payload the rail renders
 * from, so a module that adds a tab is reachable by its old deep link the same
 * day without anyone editing an edge rule.
 *
 * It replaces rather than pushes: the legacy URL is not a place in the person's
 * history, and leaving it there means Back walks them into the redirect again.
 *
 * A host the registry does not know resolves to nothing, and this page SAYS so.
 * Bouncing an unrecognised link to the portal root would silently lose the
 * place someone had bookmarked, and they would have eight areas to search.
 */
export function LegacyRedirectPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const landing = parseLegacyLanding(location.pathname);

  const probe = useQuery({
    queryKey: ["me"],
    queryFn: ({ signal }) => probeMe(signal),
    retry: false,
  });
  const me = probe.data?.kind === "me" ? probe.data.me : null;

  const target = me && landing ? resolveLegacyTarget(me.areas, landing.host, landing.path) : null;

  useEffect(() => {
    if (!target) return;
    // Two things are carried across, and dropping either lands the person on
    // the right screen showing the wrong thing:
    //   - the original query string (and fragment) — for a docked surface they
    //     belonged to it (a canvas `?screen=Q2`), so they ride into the frame
    //     with `at`; a native module keeps reading its filters off the route;
    //   - `at`, the path INSIDE a docked surface, which no portal route can
    //     express (bug 3d40402f — a Canvas share link opened the library grid
    //     instead of the artefact it named).
    // `legacyDestination` decides where the query goes: into the docked surface
    // when the link opens one (bug 3fc0402f), else onto the route.
    void navigate({ to: legacyDestination(target, location.searchStr, location.hash), replace: true });
  }, [target, navigate, location.searchStr, location.hash]);

  return (
    <div className="flex min-h-dvh flex-col">
      <TopBar me={me} canAdminister={me?.can_administer ?? false} />
      <main className="flex-1 p-8">
        {probe.isPending ? (
          <Skeleton className="h-8 w-64" />
        ) : target ? (
          <p className="text-ks-ink">Taking you there&hellip;</p>
        ) : (
          <Alert className="max-w-2xl">
            <AlertTitle>That link&rsquo;s old home has moved</AlertTitle>
            <AlertDescription>
              {landing
                ? `${landing.host}.ketosource.co is now part of the portal, but nothing in your areas matches ${landing.path}. Pick the area from the rail, or ask its owner.`
                : "We couldn't read that link. Pick the area from the rail."}
            </AlertDescription>
          </Alert>
        )}
      </main>
    </div>
  );
}
